import type { components } from "@dolfin/api-admin";

export type MatchOutcomeBucketKey = components["schemas"]["MatchOutcomeBucket"];

export interface MatchOutcomeBucketSpec {
  key: MatchOutcomeBucketKey;
  /** Plain-English name. No internal vocabulary: never "SKU", "provenance", "allocation" or "engine". */
  label: string;
}

/** The five recorded match methods in display order, in plain words. Detail only: shown in Data details. */
export const MATCH_OUTCOME_BUCKETS: MatchOutcomeBucketSpec[] = [
  { key: "AutoSku", label: "Automatic: product code" },
  { key: "AutoSimilarity", label: "Automatic: similar description" },
  { key: "AutoLinkedPo", label: "Automatic: linked order" },
  { key: "Confirmed", label: "Suggestion approved" },
  { key: "Manual", label: "Matched manually" },
];

const BUCKET_LABELS = new Map(MATCH_OUTCOME_BUCKETS.map((bucket) => [bucket.key, bucket.label]));

/** Label for one raw bucket, falling back to the raw key if the backend adds a sixth before we do. */
export function bucketLabel(key: MatchOutcomeBucketKey | string): string {
  return BUCKET_LABELS.get(key as MatchOutcomeBucketKey) ?? key;
}

export type MatchOutcomeGroupKey = "Automatic" | "Confirmed" | "Manual";

export interface MatchOutcomeGroupSpec {
  key: MatchOutcomeGroupKey;
  label: string;
  /** A `var(--series-N)` reference into MATCH_VIZ_STYLES below - never a literal hex in a chart file. */
  color: string;
  /** Which of the five recorded methods roll up into this group. */
  members: MatchOutcomeBucketKey[];
}

/** The three categories every default view uses. Colours are palette slots 1-3 (as DocumentVolumeChart);
 * adjacent pairs pass scripts/validate_palette.js in light and dark. */
export const MATCH_OUTCOME_GROUPS: MatchOutcomeGroupSpec[] = [
  {
    key: "Automatic",
    label: "Matched automatically",
    color: "var(--series-1)",
    members: ["AutoSku", "AutoSimilarity", "AutoLinkedPo"],
  },
  { key: "Confirmed", label: "Suggestion approved", color: "var(--series-2)", members: ["Confirmed"] },
  { key: "Manual", label: "Matched manually", color: "var(--series-3)", members: ["Manual"] },
];

/** The three automatic methods, for the split shown inside Data details. */
export const AUTOMATIC_BUCKETS: MatchOutcomeBucketSpec[] = MATCH_OUTCOME_BUCKETS.filter((bucket) =>
  MATCH_OUTCOME_GROUPS[0]!.members.includes(bucket.key),
);

const GROUP_OF_BUCKET = new Map<MatchOutcomeBucketKey, MatchOutcomeGroupKey>(
  MATCH_OUTCOME_GROUPS.flatMap((group) => group.members.map((member) => [member, group.key] as const)),
);

/** Which of the three headline categories a recorded method belongs to. */
export function groupOfBucket(key: MatchOutcomeBucketKey): MatchOutcomeGroupKey | undefined {
  return GROUP_OF_BUCKET.get(key);
}

export const MATCH_VIZ_CLASS = "dolfin-match-outcome-viz";

export const MATCH_VIZ_STYLES = `
.${MATCH_VIZ_CLASS} {
  --viz-surface: #fcfcfb;
  --series-1: #2a78d6;
  --series-2: #eb6834;
  --series-3: #1baf7a;
}
.dark .${MATCH_VIZ_CLASS} {
  --viz-surface: #1a1a19;
  --series-1: #3987e5;
  --series-2: #d95926;
  --series-3: #199e70;
}
`;


export function toChartNumber(value: number | string | undefined | null): number {
  if (value === undefined || value === null) return 0;
  const num = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(num) ? num : 0;
}

/** Explains what the two counting units mean, wherever the choice is offered. */
export const COUNT_BY_HINT = "Bill items counts each matched line. Bills counts each bill once.";

/** Survivorship caveat: an unpicked match is deleted outright, so figures describe surviving matches only. */
export const MATCH_SURVIVORSHIP_NOTE =
  "A match that someone unpicks is deleted, and nothing records that it existed. Everything here counts matches that still exist, which flatters the automatic figures, because removals usually correct a mistake the system made.";
